import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";

import "./styles/global.css";
import "./i18n";
import App from "./App";
import { HomeView } from "./components/HomeView";
import { DirectoryView } from "./components/DirectoryView";
import { DirectoryPicker } from "./components/DirectoryPicker";
import { ClosedDirectoryView } from "./components/ClosedDirectoryView";
import { WhanauProfile } from "./components/WhanauProfile";
import { AdminUsers } from "./components/AdminUsers";
import { AdminSettings } from "./components/AdminSettings";
import { AuditLog } from "./components/AuditLog";
import { FrameworkPage } from "./components/FrameworkPage";
import { ManagerSettings } from "./components/ManagerSettings";
import { PermissionsMatrix } from "./components/PermissionsMatrix";
import { AppearanceSettings } from "./components/AppearanceSettings";
import { StateAppearance } from "./components/StateAppearance";
import { FrameworkEditor } from "./components/FrameworkEditor";
import { ManagerCaseload } from "./components/ManagerCaseload";
import { ManagerSignoffs } from "./components/ManagerSignoffs";
import { ManagerViews } from "./components/ManagerViews";
import { ReportsPage } from "./reporting/ReportsPage";
import { ExitSurveyPage } from "./survey/ExitSurveyPage";
import { SignoffActionPage } from "./signoff/SignoffActionPage";
import { msalConfig } from "./auth/msalConfig";
import {
  initAppInsights,
  setTelemetryUser,
  clearTelemetryUser,
} from "./telemetry/appInsights";
import { AppInsightsProvider } from "./telemetry/AppInsightsProvider";

/**
 * MSAL.js v3 requires `initialize()` before any other API call,
 * and `handleRedirectPromise()` to clear any in-flight redirect responses.
 *
 * Bootstrap order:
 *   1. Construct PublicClientApplication
 *   2. await initialize()
 *   3. Set the default account if a sign-in completed
 *   4. await handleRedirectPromise() — for the redirect-flow fallback path
 *   5. Render the app inside MsalProvider + RouterProvider
 *
 * Routing posture: data router (createBrowserRouter + RouterProvider)
 * rather than declarative <BrowserRouter>. The data router is required
 * for useBlocker (dirty-state navigation guard on the profile editor)
 * and unlocks route loaders / actions for future features.
 */
async function bootstrap(): Promise<void> {
  // Initialise Application Insights first so the SDK is ready to observe
  // MSAL bootstrap, the first route, and any errors during render.
  // Returns null in environments without VITE_APPINSIGHTS_CONNECTION_STRING
  // set (typical local dev) — the telemetry API is a no-op in that case.
  const appInsights = initAppInsights();

  const msalInstance = new PublicClientApplication(msalConfig);

  await msalInstance.initialize();

  // If we already have an account from a previous session, set it as active.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
    // Bind the AI user context to the OID so SPA telemetry correlates
    // with API telemetry by kaimahi.
    if (accounts[0].homeAccountId) {
      setTelemetryUser(appInsights, accounts[0].localAccountId);
    }
  }

  // Keep the active account in sync with sign-in events, and propagate
  // user identity to AI so telemetry is correlatable to the kaimahi.
  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      event.payload &&
      "account" in event.payload &&
      event.payload.account
    ) {
      msalInstance.setActiveAccount(event.payload.account);
      setTelemetryUser(appInsights, event.payload.account.localAccountId);
    }
    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      clearTelemetryUser(appInsights);
    }
  });

  // Process any redirect response (no-op for popup flow, harmless to call).
  await msalInstance.handleRedirectPromise();

  // Routes are declared here rather than in JSX inside <App />.
  // <App /> is the shared layout: header + footer + auth gating. Each
  // route renders into <Outlet /> inside that layout.
  const router = createBrowserRouter([
    // Public, unauthenticated whānau exit survey — a sibling of <App /> so it
    // sits outside the auth gate and app chrome (ADR-008).
    { path: "/survey/exit", element: <ExitSurveyPage /> },
    // Public supervisor sign-off action page — one-tap Approve/Return from the
    // email, gated by a single-use token (ADR-013 §3). Also a sibling of <App />.
    { path: "/signoff/action", element: <SignoffActionPage /> },
    {
      element: <App />,
      children: [
        { path: "/", element: <HomeView /> },
        { path: "/directory", element: <DirectoryPicker /> },
        { path: "/directory/open", element: <DirectoryView /> },
        { path: "/directory/closed", element: <ClosedDirectoryView /> },
        { path: "/whanau/:whanauId", element: <WhanauProfile /> },
        { path: "/manager", element: <ManagerSettings /> },
        { path: "/manager/caseload", element: <ManagerCaseload /> },
        { path: "/manager/signoffs", element: <ManagerSignoffs /> },
        { path: "/manager/views", element: <ManagerViews /> },
        { path: "/reports", element: <ReportsPage /> },
        { path: "/admin", element: <AdminSettings /> },
        { path: "/admin/users", element: <AdminUsers /> },
        { path: "/admin/audit", element: <AuditLog /> },
        { path: "/admin/permissions", element: <PermissionsMatrix /> },
        { path: "/admin/appearance", element: <AppearanceSettings /> },
        { path: "/admin/states", element: <StateAppearance /> },
        { path: "/admin/framework", element: <FrameworkEditor /> },
        { path: "/framework", element: <FrameworkPage /> },
      ],
    },
  ]);

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <AppInsightsProvider instance={appInsights}>
        <MsalProvider instance={msalInstance}>
          <RouterProvider router={router} />
        </MsalProvider>
      </AppInsightsProvider>
    </StrictMode>,
  );
}

void bootstrap();
